/* eslint-disable camelcase */
import { useEffect, useState, type ComponentProps } from "react";
import { axios, isAxiosError } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import type { RecommendWorks } from "@userFrontend/features/promotion/components/RecommendWorks";
import { omitEmploymentName } from "@root/userFrontend/utils/formatSalary";

type RecommendWorksProps = ComponentProps<typeof RecommendWorks>;

type ErrorResponse = {
  error: string;
};

/** API レスポンスの型定義 */
type SalesAssessmentSalary = {
  employmentTypeName: string;
  salaryTypeName: string;
  minPrice: string;
  minUnit: string;
  maxPrice: string;
  maxUnit: string;
};

type SalesAssessmentCassette = {
  imageUrl: string;
  shopNameAndOccupation: string;
  title: string;
  salaries: SalesAssessmentSalary[];
  shop_address: string;
  nearestStationDisplay: string;
  detailUrl: string;
};

type SalesAssessmentCategory = {
  link: string;
  cassettes: SalesAssessmentCassette[];
};

type SalesAssessmentRecommendResponse = {
  ekichika: SalesAssessmentCategory;
  training_system: SalesAssessmentCategory;
  over_10k: SalesAssessmentCategory;
};

const ERROR_MESSAGE = "おすすめ求人の取得に失敗しました";

/** カテゴリキーと表示用情報のマッピング */
const CATEGORY_CONFIG: Record<
  keyof SalesAssessmentRecommendResponse,
  { title: string; description: string; gaActionName: string }
> = {
  ekichika: {
    title: "対応客数を伸ばしたい方へ",
    description: "駅近で集客力のある求人",
    gaActionName: "assess_result__customer",
  },
  training_system: {
    title: "指名割合を伸ばしたい方へ",
    description: "充実した研修制度がある求人",
    gaActionName: "assess_result__nomination",
  },
  over_10k: {
    title: "顧客単価を伸ばしたい方へ",
    description: "客単価10,000円以上の求人",
    gaActionName: "assess_result__spending",
  },
};

/**
 * shop_address から都道府県、市区町村、駅情報をパースする
 * 例: "東京都 新宿区(新宿駅 徒歩1分)" → { prefecture: "東京都", city: "新宿区", station: "新宿駅 徒歩1分" }
 */
const parseShopAddress = (
  shopAddress: string,
  nearestStationDisplay: string,
): { prefecture: string; city: string; station: string } => {
  // "東京都 新宿区(新宿駅 徒歩1分)" のような形式をパース
  const match = /^(.+?)\s+(.+?)\(/.exec(shopAddress);
  if (match) {
    return {
      prefecture: match[1],
      city: match[2],
      station: nearestStationDisplay,
    };
  }
  // パースに失敗した場合はそのまま返す
  return {
    prefecture: "",
    city: shopAddress,
    station: nearestStationDisplay,
  };
};

/**
 * SalesAssessmentSalary を Salaries コンポーネントの employmentAndSalaryType に変換
 */
const convertSalary = (
  salary: SalesAssessmentSalary,
): {
  employmentName: string;
  salaryTypeName: string;
  salaryMin: string;
  salaryMinUnit: string;
  salaryMax: string;
  salaryMaxUnit: string;
} => ({
  employmentName: omitEmploymentName(salary.employmentTypeName),
  salaryTypeName: salary.salaryTypeName,
  salaryMin: salary.minPrice,
  salaryMinUnit: salary.minUnit,
  salaryMax: salary.maxPrice,
  salaryMaxUnit: salary.maxUnit,
});

/**
 * SalesAssessmentCassette を NoButtonWorkCassette の props に変換
 */
const convertCassette = (
  cassette: SalesAssessmentCassette,
): RecommendWorksProps["works"][number] => ({
  url: cassette.detailUrl,
  imgSrc: cassette.imageUrl,
  shopName: cassette.shopNameAndOccupation,
  title: cassette.title,
  shopAddress: parseShopAddress(cassette.shop_address, cassette.nearestStationDisplay),
  employmentAndSalaryType: cassette.salaries.map(convertSalary),
});

/**
 * SalesAssessmentCategory を RecommendWorks の props に変換
 */
const convertCategory = (
  categoryKey: keyof SalesAssessmentRecommendResponse,
  category: SalesAssessmentCategory,
): RecommendWorksProps => ({
  title: CATEGORY_CONFIG[categoryKey].title,
  description: CATEGORY_CONFIG[categoryKey].description,
  gaActionPrefix: CATEGORY_CONFIG[categoryKey].gaActionName,
  linkPath: category.link,
  works: category.cassettes.map(convertCassette),
});

/**
 * API レスポンスを RecommendWorks[] に変換
 * カテゴリの順序: ekichika → training_system → over_10k
 */
const convertResponse = (response: SalesAssessmentRecommendResponse): RecommendWorksProps[] => [
  convertCategory("ekichika", response.ekichika),
  convertCategory("training_system", response.training_system),
  convertCategory("over_10k", response.over_10k),
];

export const useRecommendWorks = (apiUrl: string) => {
  const [recommendWorks, setRecommendWorks] = useState<RecommendWorksProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchRecommendWorks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get<SalesAssessmentRecommendResponse>(apiUrl, {
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;

        setRecommendWorks(convertResponse(response.data));
      } catch (err) {
        if (abortController.signal.aborted) return;

        if (isAxiosError<ErrorResponse>(err)) {
          const errorMessage = err.response?.data?.error ?? ERROR_MESSAGE;
          sendErrorLog(new Error(errorMessage));
        } else {
          sendErrorLog(new Error(ERROR_MESSAGE));
        }
        setError("時間をおいて再度お試しください");
        setRecommendWorks([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchRecommendWorks();

    return () => abortController.abort();
  }, [apiUrl]);

  return { recommendWorks, isLoading, error };
};
